import type { QueryClient } from '@tanstack/react-query';
import { useEffect, type ReactElement } from 'react';
import { redirect, useLoaderData, useNavigate } from 'react-router';
import { homeQuery, safeReturnTo, waitingQuery } from '../api';
import { photos } from '../assets';
import { prefersReducedMotion } from '../design/motion';
import { requireMe } from '../session/loaders';
import { startHref } from '../session/presenter';
import { cx } from '../ui';
import s from './WelcomePage.module.css';

export interface WelcomeData {
  next: string;
  greetingName: string;
}

/**
 * After Microsoft Entra ID hands back. Validates `next` (same-site paths only), loads the person
 * (a lost session goes back to sign-in, returning to `next`) and starts loading Home underneath.
 * With reduced motion there's no moment at all: straight on to `next`.
 *
 * Sign-in with no page of its own to return to (`/`) lands on the person's start page (SET1); a
 * link they followed (an email's) still opens its own page.
 */
export async function welcomeLoader(
  queryClient: QueryClient,
  request: Request,
): Promise<WelcomeData> {
  const asked = safeReturnTo(new URL(request.url).searchParams.get('next'));
  const me = await requireMe(queryClient, request, asked);
  const next = asked === '/' ? startHref(me) : asked;
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router loaders redirect by throwing a Response.
  if (prefersReducedMotion()) throw redirect(next);
  if (next === '/') {
    void Promise.all([queryClient.query(waitingQuery), queryClient.query(homeQuery(me))]).catch(
      () => undefined,
    );
  }
  return { next, greetingName: me.person.greetingName };
}

/** When Home takes over (G2 option A, "Focus pull"): the name rises, the rule draws, then Home. */
export const WELCOME_MS = 3400;

/** The same photos as the sign-in wall, softened into a backdrop. */
const BACKDROP = [
  photos.colleaguesChattingInAtrium,
  photos.groupOfEmployeesLaughing,
  photos.areteBandOnStage,
  photos.womanEngineerTestingElectronics,
  photos.engineersAtLidarFieldTest,
  photos.engineersAtWhiteboard,
  photos.navyMineDetectionAtSea,
  photos.employeeInAreteGear,
  photos.opticalFieldTesting,
] as const;

/**
 * The moment between sign-in and Home (G2 option A): the photo wall blurs and darkens, "Welcome,
 * Jordan." rises into focus, the gradient rule draws beneath it, and Home cross-fades in. No
 * button, no stars, no confetti.
 */
export function WelcomePage(): ReactElement {
  const { next, greetingName } = useLoaderData<typeof welcomeLoader>();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void navigate(next, { replace: true, viewTransition: true });
    }, WELCOME_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [navigate, next]);

  return (
    <div className={s.page}>
      <title>Welcome · Areté MyRewards</title>
      <div className={s.backdrop} aria-hidden="true">
        {BACKDROP.map((src) => (
          <img key={src} src={src} alt="" className={s.photo} decoding="async" />
        ))}
      </div>
      <div className={s.vignette} aria-hidden="true" />
      <main className={s.content}>
        <p className={s.wordmark} aria-hidden="true">
          My<span className={s.wordmarkGold}>Rewards</span>
        </p>
        <h1 className={cx('disp', s.name)} data-testid="welcome-text">
          Welcome, {greetingName}.
        </h1>
        <span className={s.rule} aria-hidden="true" />
      </main>
    </div>
  );
}
